import MapInfo from '@/model/MapInfo'
import { ref } from 'vue'
import type StoryTree from '@/model/StoryTree'
import type EventBase from '@/model/EventClass/EventBase'
import { ensureEventInstance, ensureEventListInstances } from '@/model/EventClass/eventFromReader'
import { reorderMapsDepthFirst } from '@/control/storyMapOperations'

export type EditorHistoryDomain = 'story' | 'event' | 'customUi'
export type EditorHistoryAction = 'create' | 'delete' | 'update' | 'move'

export interface EditorHistorySelection {
  sceneId: number | null
  eventIndex: number | null
}

export interface EditorHistoryApplyResult {
  selection: EditorHistorySelection
  domain: EditorHistoryDomain
  restoredEventList: boolean
}

export interface EditorHistoryMapSnapshot {
  id: number
  parentId: number
  order: number
  name: string
  isHard: boolean
  canSave: boolean
}

export interface EditorHistoryMapBundle extends EditorHistoryMapSnapshot {
  events: unknown[]
}

export interface EditorHistoryEventItem {
  index: number
  event: unknown
}

export interface EditorHistoryEventRange {
  start: number
  events: unknown[]
}

interface EditorHistoryBaseRecord {
  id: string
  domain: EditorHistoryDomain
  action: EditorHistoryAction
  label: string
  createdAt: number
  beforeSelection: EditorHistorySelection
  afterSelection: EditorHistorySelection
}

export type EditorHistoryRecord =
  | (EditorHistoryBaseRecord & {
    domain: 'story'
    action: 'create'
    maps: EditorHistoryMapBundle[]
  })
  | (EditorHistoryBaseRecord & {
    domain: 'story'
    action: 'delete'
    maps: EditorHistoryMapBundle[]
  })
  | (EditorHistoryBaseRecord & {
    domain: 'story'
    action: 'update' | 'move'
    beforeMaps: EditorHistoryMapSnapshot[]
    afterMaps: EditorHistoryMapSnapshot[]
  })
  | (EditorHistoryBaseRecord & {
    domain: 'event'
    action: 'create'
    mapId: number
    index: number
    events: unknown[]
    beforeList?: unknown[]
    afterList?: unknown[]
    beforeRanges?: EditorHistoryEventRange[]
    afterRanges?: EditorHistoryEventRange[]
  })
  | (EditorHistoryBaseRecord & {
    domain: 'event'
    action: 'delete'
    mapId: number
    items: EditorHistoryEventItem[]
    beforeList?: unknown[]
    afterList?: unknown[]
    beforeRanges?: EditorHistoryEventRange[]
    afterRanges?: EditorHistoryEventRange[]
  })
  | (EditorHistoryBaseRecord & {
    domain: 'event'
    action: 'update'
    mapId: number
    index: number
    beforeEvent: unknown
    afterEvent: unknown
    beforeList?: unknown[]
    afterList?: unknown[]
    beforeRanges?: EditorHistoryEventRange[]
    afterRanges?: EditorHistoryEventRange[]
  })
  | (EditorHistoryBaseRecord & {
    domain: 'event'
    action: 'move'
    mapId: number
    fromItems: EditorHistoryEventItem[]
    toIndex: number
    afterEvents: unknown[]
    beforeList?: unknown[]
    afterList?: unknown[]
    beforeRanges?: EditorHistoryEventRange[]
    afterRanges?: EditorHistoryEventRange[]
  })
  | (EditorHistoryBaseRecord & {
    domain: 'customUi'
    action: 'update'
    beforeList: unknown[]
    afterList: unknown[]
  })

type NewEditorHistoryRecord = EditorHistoryRecord extends infer T
  ? T extends EditorHistoryRecord
    ? Omit<T, 'id' | 'createdAt'>
    : never
  : never

export interface PendingEventUpdateHistory {
  mapId: number
  index: number
  beforeEvent: unknown
  beforeList: unknown[]
  beforeRanges: EditorHistoryEventRange[]
  beforeSelection: EditorHistorySelection
}

export type EditorHistoryEventListResolver = (mapId: number) => EventBase[] | null
export type EditorHistoryCustomUiApplier = (list: unknown[]) => void

function clonePlain<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function createHistoryId(): string {
  return `history-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

function cloneEvent(event: unknown): unknown {
  return clonePlain(event)
}

function reviveEvent(event: unknown): EventBase {
  return ensureEventInstance(clonePlain(event) as EventBase)
}

export function cloneHistoryEvents(events: Iterable<unknown>): unknown[] {
  return [...events].map((event) => cloneEvent(event))
}

export function captureMapHistorySnapshot(map: MapInfo): EditorHistoryMapSnapshot {
  return {
    id: map.Id,
    parentId: map.ParentId,
    order: map.Order,
    name: map.Name,
    isHard: map.IsHard,
    canSave: map.canSave,
  }
}

export function captureMapHistoryBundle(st: StoryTree, map: MapInfo): EditorHistoryMapBundle {
  return {
    ...captureMapHistorySnapshot(map),
    events: cloneHistoryEvents(st.loadEvent(map.Id)),
  }
}

export function captureEventHistoryItems(events: EventBase[], indices: number[]): EditorHistoryEventItem[] {
  return indices
    .map((index) => {
      const event = events[index]
      return event ? { index, event: cloneEvent(event) } : null
    })
    .filter((item): item is EditorHistoryEventItem => item !== null)
}

export function captureEventHistoryRange(events: EventBase[], index: number, count: number): unknown[] {
  return cloneHistoryEvents(events.slice(index, index + count))
}

export function captureEventHistoryRangeSnapshot(events: EventBase[], start: number, count: number): EditorHistoryEventRange {
  return {
    start,
    events: captureEventHistoryRange(events, start, count),
  }
}

export function captureEventHistoryRanges(events: EventBase[], indices: number[]): EditorHistoryEventRange[] {
  const sorted = [...indices].sort((a, b) => a - b)
  const ranges: EditorHistoryEventRange[] = []
  let start = -1
  let end = -1
  for (const index of sorted) {
    if (index < 0 || index >= events.length) continue
    if (start < 0) {
      start = index
      end = index
      continue
    }
    if (index === end + 1) {
      end = index
      continue
    }
    ranges.push(captureEventHistoryRangeSnapshot(events, start, end - start + 1))
    start = index
    end = index
  }
  if (start >= 0) {
    ranges.push(captureEventHistoryRangeSnapshot(events, start, end - start + 1))
  }
  return ranges
}

function applyMapSnapshot(map: MapInfo, snapshot: EditorHistoryMapSnapshot): void {
  map.Id = snapshot.id
  map.ParentId = snapshot.parentId
  map.Order = snapshot.order
  map.Name = snapshot.name
  map.IsHard = snapshot.isHard
  map.canSave = snapshot.canSave
}

function createMapFromBundle(bundle: EditorHistoryMapBundle): MapInfo {
  const list = ensureEventListInstances(bundle.events.map((event) => reviveEvent(event)))
  const map = new MapInfo({
    name: bundle.name,
    id: bundle.id,
    parentId: bundle.parentId,
    order: bundle.order,
    list,
  })
  map.IsHard = bundle.isHard
  map.canSave = bundle.canSave
  return map
}

function upsertMapBundles(st: StoryTree, bundles: EditorHistoryMapBundle[]): void {
  const byId = new Map(st.Maps.map((map) => [map.Id, map]))
  const maps = [...st.Maps]
  for (const bundle of bundles) {
    const existing = byId.get(bundle.id)
    const list = ensureEventListInstances(bundle.events.map((event) => reviveEvent(event)))
    if (existing) {
      applyMapSnapshot(existing, bundle)
      existing.List = list
      st.Events[String(bundle.id)] = list
    } else {
      const map = createMapFromBundle(bundle)
      maps.push(map)
      st.Events[String(bundle.id)] = map.List
    }
  }
  st.createStoryTree(maps)
  reorderMapsDepthFirst(st)
}

function removeMaps(st: StoryTree, ids: number[]): void {
  const removing = new Set(ids)
  st.Maps = st.Maps.filter((map) => !removing.has(map.Id))
  for (const id of removing) {
    delete st.Events[String(id)]
  }
  st.createStoryTree()
  reorderMapsDepthFirst(st)
}

function applyMapSnapshots(st: StoryTree, snapshots: EditorHistoryMapSnapshot[]): void {
  const byId = new Map(st.Maps.map((map) => [map.Id, map]))
  for (const snapshot of snapshots) {
    const map = byId.get(snapshot.id)
    if (map) applyMapSnapshot(map, snapshot)
  }
  st.createStoryTree()
  reorderMapsDepthFirst(st)
}

function getEventList(st: StoryTree, mapId: number, resolver?: EditorHistoryEventListResolver): EventBase[] {
  return resolver?.(mapId) ?? st.loadEvent(mapId)
}

function applyEventListSnapshot(list: EventBase[], snapshot: unknown[]): void {
  list.splice(0, list.length, ...snapshot.map((event) => reviveEvent(event)))
}

function applyEventRangeSnapshots(
  list: EventBase[],
  removeRanges: EditorHistoryEventRange[],
  insertRanges: EditorHistoryEventRange[],
): void {
  for (const range of [...removeRanges].sort((a, b) => b.start - a.start)) {
    list.splice(range.start, range.events.length)
  }
  for (const range of [...insertRanges].sort((a, b) => a.start - b.start)) {
    list.splice(range.start, 0, ...range.events.map((event) => reviveEvent(event)))
  }
}

function insertEventItems(st: StoryTree, mapId: number, items: EditorHistoryEventItem[], resolver?: EditorHistoryEventListResolver): void {
  const list = getEventList(st, mapId, resolver)
  for (const item of [...items].sort((a, b) => a.index - b.index)) {
    list.splice(item.index, 0, reviveEvent(item.event))
  }
}

function removeEventItems(st: StoryTree, mapId: number, indices: number[], resolver?: EditorHistoryEventListResolver): void {
  const list = getEventList(st, mapId, resolver)
  for (const index of [...indices].sort((a, b) => b - a)) {
    list.splice(index, 1)
  }
}

function insertEventRange(st: StoryTree, mapId: number, index: number, events: unknown[], resolver?: EditorHistoryEventListResolver): void {
  getEventList(st, mapId, resolver).splice(index, 0, ...events.map((event) => reviveEvent(event)))
}

function removeEventRange(st: StoryTree, mapId: number, index: number, count: number, resolver?: EditorHistoryEventListResolver): void {
  getEventList(st, mapId, resolver).splice(index, count)
}

function replaceEvent(st: StoryTree, mapId: number, index: number, event: unknown, resolver?: EditorHistoryEventListResolver): void {
  const list = getEventList(st, mapId, resolver)
  if (index < 0 || index >= list.length) return
  list.splice(index, 1, reviveEvent(event))
}

function applyRecord(
  st: StoryTree,
  record: EditorHistoryRecord,
  direction: 'undo' | 'redo',
  resolver?: EditorHistoryEventListResolver,
  applyCustomUi?: EditorHistoryCustomUiApplier,
): EditorHistorySelection {
  if (record.domain === 'story') {
    if (record.action === 'create') {
      if (direction === 'undo') removeMaps(st, record.maps.map((map) => map.id))
      else upsertMapBundles(st, record.maps)
    } else if (record.action === 'delete') {
      if (direction === 'undo') upsertMapBundles(st, record.maps)
      else removeMaps(st, record.maps.map((map) => map.id))
    } else {
      applyMapSnapshots(st, direction === 'undo' ? record.beforeMaps : record.afterMaps)
    }
  } else if (record.domain === 'customUi') {
    applyCustomUi?.(direction === 'undo' ? record.beforeList : record.afterList)
  } else if (record.beforeList && record.afterList) {
    applyEventListSnapshot(
      getEventList(st, record.mapId, resolver),
      direction === 'undo' ? record.beforeList : record.afterList,
    )
  } else if (record.beforeRanges && record.afterRanges) {
    applyEventRangeSnapshots(
      getEventList(st, record.mapId, resolver),
      direction === 'undo' ? record.afterRanges : record.beforeRanges,
      direction === 'undo' ? record.beforeRanges : record.afterRanges,
    )
  } else if (record.action === 'create') {
    if (direction === 'undo') removeEventRange(st, record.mapId, record.index, record.events.length, resolver)
    else insertEventRange(st, record.mapId, record.index, record.events, resolver)
  } else if (record.action === 'delete') {
    if (direction === 'undo') insertEventItems(st, record.mapId, record.items, resolver)
    else removeEventItems(st, record.mapId, record.items.map((item) => item.index), resolver)
  } else if (record.action === 'update') {
    replaceEvent(st, record.mapId, record.index, direction === 'undo' ? record.beforeEvent : record.afterEvent, resolver)
  } else {
    if (direction === 'undo') {
      removeEventRange(st, record.mapId, record.toIndex, record.afterEvents.length, resolver)
      insertEventItems(st, record.mapId, record.fromItems, resolver)
    } else {
      removeEventItems(st, record.mapId, record.fromItems.map((item) => item.index), resolver)
      insertEventRange(st, record.mapId, record.toIndex, record.afterEvents, resolver)
    }
  }
  return direction === 'undo' ? record.beforeSelection : record.afterSelection
}

function isSameRecordData(record: NewEditorHistoryRecord): boolean {
  if (record.domain === 'story' && (record.action === 'update' || record.action === 'move')) {
    return JSON.stringify(record.beforeMaps) === JSON.stringify(record.afterMaps)
  }
  if (record.domain === 'event' && record.beforeList && record.afterList) {
    return JSON.stringify(record.beforeList) === JSON.stringify(record.afterList)
  }
  if (record.domain === 'event' && record.beforeRanges && record.afterRanges) {
    return JSON.stringify(record.beforeRanges) === JSON.stringify(record.afterRanges)
  }
  if (record.domain === 'event' && record.action === 'update') {
    return JSON.stringify(record.beforeEvent) === JSON.stringify(record.afterEvent)
  }
  if (record.domain === 'customUi') {
    return JSON.stringify(record.beforeList) === JSON.stringify(record.afterList)
  }
  return false
}

export function createEditorHistory(maxCount = 100) {
  const undoStack: EditorHistoryRecord[] = []
  const redoStack: EditorHistoryRecord[] = []
  const revision = ref(0)
  let applying = false

  function touch(): void {
    revision.value++
  }

  function push(record: NewEditorHistoryRecord): boolean {
    if (applying || isSameRecordData(record)) return false
    undoStack.push({
      ...record,
      id: createHistoryId(),
      createdAt: Date.now(),
    })
    if (undoStack.length > maxCount) undoStack.shift()
    redoStack.length = 0
    touch()
    return true
  }

  function undo(
    st: StoryTree,
    resolver?: EditorHistoryEventListResolver,
    applyCustomUi?: EditorHistoryCustomUiApplier,
  ): EditorHistoryApplyResult | null {
    const record = undoStack.pop()
    if (!record) return null
    applying = true
    try {
      const selection = applyRecord(st, record, 'undo', resolver, applyCustomUi)
      redoStack.push(record)
      touch()
      return { selection, domain: record.domain, restoredEventList: record.domain === 'event' && !!record.beforeList && !!record.afterList }
    } finally {
      applying = false
    }
  }

  function redo(
    st: StoryTree,
    resolver?: EditorHistoryEventListResolver,
    applyCustomUi?: EditorHistoryCustomUiApplier,
  ): EditorHistoryApplyResult | null {
    const record = redoStack.pop()
    if (!record) return null
    applying = true
    try {
      const selection = applyRecord(st, record, 'redo', resolver, applyCustomUi)
      undoStack.push(record)
      touch()
      return { selection, domain: record.domain, restoredEventList: record.domain === 'event' && !!record.beforeList && !!record.afterList }
    } finally {
      applying = false
    }
  }

  function clear(): void {
    undoStack.length = 0
    redoStack.length = 0
    touch()
  }

  return {
    undoStack,
    redoStack,
    revision,
    get isApplying() {
      return applying
    },
    push,
    undo,
    redo,
    clear,
  }
}
